/**
 * Who is signed in, and what the decision point says they may do.
 *
 * Both come from the server, and neither is inferred here. The capability
 * report is advisory — it decides what the application *offers*, never what it
 * is *allowed* to do — and every call it leads to is authorized again at the
 * endpoint. A frontend that reasoned about roles would be a second
 * authorization system that ships to a browser and cannot be audited.
 */

import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { api } from "../api/client";
import { useResource, type Resource } from "../api/useResource";
import type { CapabilityProjection, Principal } from "../api/types";

export interface Session {
  principal: Principal;
  /** Whether the decision point permitted this capability for this caller. */
  may: (key: string) => boolean;
  reload: () => void;
}

const SessionContext = createContext<Session | null>(null);

interface Loaded {
  principal: Principal;
  report: CapabilityProjection;
}

export function SessionProvider({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback: (state: Resource<Loaded>, reload: () => void) => ReactNode;
}) {
  const read = useCallback(async (signal: AbortSignal): Promise<Loaded> => {
    const [principal, report] = await Promise.all([
      api.get<Principal>("/identity/me", { signal, onUnauthenticated: "throw" }),
      api.get<CapabilityProjection>("/authorization/capabilities", { signal, onUnauthenticated: "throw" }),
    ]);
    return { principal, report };
  }, []);

  const { state, reload } = useResource<Loaded>(read);

  const value = useMemo<Session | null>(() => {
    if (state.kind !== "ready") return null;
    const permitted = new Set(
      state.value.report.capabilities.filter((row) => row.permitted).map((row) => row.key),
    );
    return {
      principal: state.value.principal,
      may: (key: string) => permitted.has(key),
      reload,
    };
  }, [state, reload]);

  if (value === null) return <>{fallback(state, reload)}</>;
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (session === null) {
    throw new Error("useSession was called outside a signed-in shell");
  }
  return session;
}
